import { useEffect, useState } from 'react';
import axios from 'axios';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';
import { Tooltip, IconButton, Button } from '@material-ui/core';
import PowerSettingsNewOutlinedIcon from '@mui/icons-material/PowerSettingsNewOutlined';
import ConfirmDialog from '../../ConfirmDialog';
import { setOpenSnackbar } from '../../../store/slices/snackbarSlice';
import { getIDToken } from '../../authentication/login/amplify';

async function setRackState(rackName, environment, identity, running) {
  const jwt = await getIDToken();
  if (jwt) {
    const url = `${process.env.REACT_APP_API}/racks/running`;
    const bodyData = {
      environment,
      identity,
      releaseName: rackName,
      running
    };

    return axios({
      url,
      method: 'post',
      data: bodyData,
      headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${jwt}` }
    });
  }
  return Promise.reject(new Error('missing jwt'));
}

SetRackStateButton.propTypes = {
  rackDetails: PropTypes.object,
  variant: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  size: PropTypes.string,
  label: PropTypes.string,
  showIcon: PropTypes.bool,
  tooltipTitle: PropTypes.string,
  sx: PropTypes.oneOfType([
    PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.func, PropTypes.object])),
    PropTypes.func,
    PropTypes.object
  ]),
  // External trigger to open the confirm dialog. Increment the value to re-open.
  openSignal: PropTypes.number
};

SetRackStateButton.defaultProps = {
  variant: 'contained',
  type: 'text',
  size: 'medium',
  label: 'Power',
  showIcon: true,
  tooltipTitle: undefined,
  disabled: false,
  rackDetails: { running: { state: 'running' } },
  openSignal: 0
};

export default function SetRackStateButton({
  rackDetails,
  variant,
  type,
  disabled,
  size,
  label,
  showIcon,
  tooltipTitle,
  sx = [],
  openSignal
}) {
  const dispatch = useDispatch();

  const [confirmDialogOpen, setDialogOpen] = useState(false);
  const identity = useSelector((state) => state.identity.value);
  const openSnackbar = (payload) => dispatch(setOpenSnackbar(payload));
  const getDesiredRunningState = () => !['', 'running'].includes(rackDetails.running.state);
  const getDesiredPowerState = () =>
    ['', 'running'].includes(rackDetails.running.state) ? 'off' : 'on';
  const getButtonColor = () => {
    switch (rackDetails.running.state) {
      case 'configuring':
        return 'warning';
      case 'running':
        return 'success';
      default:
        return 'secondary';
    }
  };

  const handleDialogCancel = () => {
    setDialogOpen(false);
  };

  const handleDialogOpen = (event) => {
    event.stopPropagation();
    setDialogOpen(true);
  };

  // Respond to an external open trigger (used by the rack Actions menu on xs).
  useEffect(() => {
    if (openSignal > 0) {
      setDialogOpen(true);
    }
    /* eslint-disable-next-line react-hooks/exhaustive-deps */
  }, [openSignal]);

  const handleDialogConfirm = () => {
    setRackState(rackDetails.name, rackDetails.region, identity, getDesiredRunningState())
      .then(() => {
        openSnackbar({
          message: `Request to set rack running state sent successfully!`
        });
      })
      .catch((error) => {
        openSnackbar({
          message: `${error.message}`,
          severity: 'error'
        });
      });
    setDialogOpen(false);
  };

  return (
    <>
      <ConfirmDialog
        data-testid="set-rack-state-confirm-button"
        title={`Turn Virtual Rack ${getDesiredPowerState()}?`}
        content={`This will set the rack VMs running state to ${getDesiredRunningState()}?`}
        onCancelHandler={handleDialogCancel}
        onConfirmHandler={handleDialogConfirm}
        isOpen={confirmDialogOpen}
      />
      <Tooltip title={tooltipTitle ?? `Turn ${getDesiredPowerState()} Vrack`} placement="top">
        <div>
          {type === 'icononly' && (
            <IconButton
              data-testid="set-rack-state-icon-button"
              onClick={handleDialogOpen}
              sx={{ ...sx }}
              color={getButtonColor()}
            >
              <PowerSettingsNewOutlinedIcon />
            </IconButton>
          )}
          {type === 'text' && (
            <Button
              data-testid="set-rack-state-button"
              onClick={handleDialogOpen}
              variant={variant}
              disabled={disabled}
              size={size}
              sx={{ ...sx }}
              startIcon={showIcon ? <PowerSettingsNewOutlinedIcon /> : undefined}
            >
              {label}
            </Button>
          )}
        </div>
      </Tooltip>
    </>
  );
}
